import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeleteOutlined, DownloadOutlined, MenuOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor } from "@dnd-kit/core";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, Col, Form, Modal, Popconfirm, Row, Space, Table } from "antd";
import { useForm } from "antd/lib/form/Form";
import moment from "moment";
import { useEffect, useState } from "react";
import { ETechnicalDefinitionType, EUserRole, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { CookieParserForm } from "./cookie-parser.js";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { useStores } from "../../../../../../../store/root.js";
import { spacing, templateFormDefaultPropsSmall } from "../../../../../../../styles/spacing.js";
import { ETechnicalDefinitionBadgeType } from "../../../../../../../types/technical-definitions/cookies.js";
import { DEFAULT_PAGINATION_TECHNICAL_DEFINITIONS } from "../../../../../../../types/technical-definitions/table.js";
import { DraggableRow } from "../../../../../../third-party/dnd/draggable-row.js";
import { SortableList } from "../../../../../../third-party/dnd/sortable-list.js";
import { PermissionCheck } from "../../../../user/permission.js";
/**
 * Sortable table for technical definitions with drag and drop support for reordering.
 */ const TechnicalDefinitionsTable = ({ columns, form, formName = [], ...restProps })=>{
    const { t } = useTranslation();
    const pointerSensor = useSensor(PointerSensor);
    const { notificationStore, rcbAuthenticationStore } = useStores();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const [isParserVisible, setParserVisible] = useState(false);
    const toggleParserModal = ()=>{
        setParserVisible(!isParserVisible);
    };
    const [parserForm] = useForm();
    const [isParseEnabled, setParseEnabled] = useState(false);
    const checkFormValidation = async ()=>{
        const type = parserForm.getFieldValue("technicalDefinitionType");
        const requiredFields = [
            "technicalDefinitionType",
            "rawTechnicalDefinitions"
        ];
        switch(type){
            case ETechnicalDefinitionType.Local:
            case ETechnicalDefinitionType.Session:
                requiredFields.push("origin");
                requiredFields.push("siteHost");
                break;
            case ETechnicalDefinitionType.Http:
                requiredFields.push("siteHost");
                break;
            default:
                break;
        }
        const errorList = parserForm.getFieldsError(requiredFields);
        const found = errorList.filter((error)=>error.errors?.length > 0);
        setParseEnabled(found?.length === 0);
    };
    const [formResults, setFormResult] = useState();
    const handleCookieParserFormData = (data)=>{
        setFormResult(data);
        parserForm.setFieldsValue({
            rawTechnicalDefinitions: ""
        });
    };
    useEffect(()=>{
        if (formResults?.parsedData) {
            // use form to add them
            let fieldCookies = form.getFieldValue(formName);
            if (!fieldCookies) {
                // init
                fieldCookies = [];
                form.setFieldValue(formName, fieldCookies);
            }
            const httpCookiesParsed = [];
            const storageItemsParsed = formResults.parsedData.map((definition)=>{
                if (definition.type === ETechnicalDefinitionType.Http) {
                    httpCookiesParsed.push(definition);
                    return undefined;
                }
                return definition;
            }).filter(Boolean);
            if (fieldCookies) {
                // Check http cookies for primary key (http type + name + host) to update duration!
                // Status Badge: Modified
                for (const cookie of httpCookiesParsed){
                    const updated = fieldCookies.find((existing)=>{
                        if (existing.type === ETechnicalDefinitionType.Http && existing.name === cookie.name && existing.host === cookie.host) {
                            // update durations
                            existing.duration = cookie.duration;
                            existing.durationUnit = cookie.durationUnit;
                            existing.isSessionDuration = cookie.isSessionDuration;
                            // if existing already has a type it was imported by parser, don't modify status
                            if (!existing.status) {
                                existing.status = ETechnicalDefinitionBadgeType.Modified;
                            }
                            return existing;
                        }
                        return false;
                    });
                    if (!updated) {
                        fieldCookies.push({
                            id: undefined,
                            orderPosition: fieldCookies.length,
                            type: ETechnicalDefinitionType.Http,
                            name: cookie.name,
                            host: cookie.host,
                            duration: cookie.duration,
                            durationUnit: cookie.durationUnit,
                            isSessionDuration: cookie.isSessionDuration,
                            status: ETechnicalDefinitionBadgeType.New,
                            purposeTranslationFlags: cookie.purposeTranslationFlags
                        });
                    }
                }
                // Storage items should not be added twice (only missing ones)
                for (const item of storageItemsParsed){
                    const found = fieldCookies.find((existing)=>{
                        if (existing.type !== ETechnicalDefinitionType.Http && existing.name === item.name) {
                            return existing;
                        }
                        return false;
                    });
                    if (!found) {
                        // add missing
                        fieldCookies.push({
                            id: undefined,
                            orderPosition: fieldCookies.length,
                            type: item.type,
                            name: item.name,
                            host: item.host,
                            duration: undefined,
                            durationUnit: undefined,
                            isSessionDuration: false,
                            status: ETechnicalDefinitionBadgeType.New,
                            purposeTranslationFlags: item.purposeTranslationFlags
                        });
                    }
                }
                // finally check if any cookie was imported with a status
                const foundWithStatus = fieldCookies.find((cookie)=>!!cookie.status);
                setHiddenColumns(foundWithStatus ? [] : [
                    TemplateMetaDataField.Status
                ]);
                // notify
                notificationStore.add({
                    type: "success",
                    message: t("Import successful!"),
                    duration: 5,
                    closable: true
                });
            }
            setFormResult(undefined); // set initial after processing
        }
    }, [
        formResults
    ]);
    const [hiddenColumns, setHiddenColumns] = useState([
        TemplateMetaDataField.Status
    ]);
    const cookieCreated = moment.utc();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Form.List, {
                name: formName,
                children: (fields, { add, remove, move })=>{
                    const tableColumns = [
                        {
                            title: t("Sort"),
                            dataIndex: "sort",
                            width: 30,
                            render: ()=>/*#__PURE__*/ _jsx(MenuOutlined, {
                                    style: {
                                        cursor: "grab",
                                        color: "#999"
                                    }
                                })
                        },
                        ...columns.filter((column)=>{
                            return !hiddenColumns.includes(column["dataIndex"]);
                        })
                    ];
                    if (hasCreatorRole) {
                        tableColumns.push({
                            title: t("Action"),
                            dataIndex: "actions",
                            render: (_, record, index)=>{
                                return /*#__PURE__*/ _jsx(Popconfirm, {
                                    placement: "leftTop",
                                    title: t("Are you sure to delete this entry?"),
                                    onConfirm: ()=>remove(index),
                                    okText: t("Yes"),
                                    cancelText: t("No"),
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                        onDoubleClick: ()=>remove(index)
                                    })
                                });
                            }
                        });
                    }
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: /*#__PURE__*/ _jsx(Row, {
                                    style: {
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: spacing.layout.content.padding,
                                        width: "100%"
                                    },
                                    children: /*#__PURE__*/ _jsx(Col, {
                                        flex: 1,
                                        style: {
                                            textAlign: "left"
                                        },
                                        children: /*#__PURE__*/ _jsxs(Space, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Button, {
                                                    onClick: ()=>add({
                                                            type: ETechnicalDefinitionType.Http,
                                                            name: null,
                                                            isSessionDuration: false,
                                                            duration: null,
                                                            durationUnit: null
                                                        }, 0),
                                                    children: t("Add")
                                                }),
                                                /*#__PURE__*/ _jsx(Button, {
                                                    icon: /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                                                    onClick: ()=>{
                                                        toggleParserModal();
                                                    },
                                                    children: t("Parse from browser")
                                                })
                                            ]
                                        })
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(DndContext, {
                                sensors: [
                                    pointerSensor
                                ],
                                onDragEnd: (event)=>{
                                    const from = fields.findIndex((field)=>field.key === +event.active.id);
                                    const to = fields.findIndex((field)=>field.key === +event.over?.id);
                                    move(from, to);
                                },
                                children: /*#__PURE__*/ _jsx(SortableList, {
                                    fields: fields,
                                    options: {
                                        disabled: !hasCreatorRole,
                                        strategy: verticalListSortingStrategy
                                    },
                                    children: /*#__PURE__*/ _jsx(Table, {
                                        columns: tableColumns,
                                        dataSource: fields,
                                        components: {
                                            body: {
                                                row: DraggableRow
                                            }
                                        },
                                        pagination: DEFAULT_PAGINATION_TECHNICAL_DEFINITIONS,
                                        rowKey: "key",
                                        ...restProps
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: /*#__PURE__*/ _jsx(Row, {
                                    style: {
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: spacing.layout.content.padding,
                                        width: "100%"
                                    },
                                    children: /*#__PURE__*/ _jsx(Col, {
                                        flex: 1,
                                        style: {
                                            textAlign: "left",
                                            marginTop: spacing.layout.content.margin
                                        },
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            onClick: ()=>add({
                                                    type: ETechnicalDefinitionType.Http,
                                                    name: null,
                                                    isSessionDuration: false,
                                                    duration: null,
                                                    durationUnit: null
                                                }, 0),
                                            children: t("Add")
                                        })
                                    })
                                })
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsx(Modal, {
                open: isParserVisible,
                title: t("Parse technical definitions from browser"),
                width: "70%",
                okText: "Import",
                okButtonProps: {
                    danger: true
                },
                onCancel: ()=>{
                    setParserVisible(false);
                },
                footer: [
                    /*#__PURE__*/ _jsx(Button, {
                        disabled: !isParseEnabled,
                        type: "primary",
                        onClick: ()=>parserForm.submit(),
                        children: t("Import")
                    }, "submit")
                ],
                maskClosable: false,
                children: /*#__PURE__*/ _jsx(Form, {
                    form: parserForm,
                    initialValues: {
                        technicalDefinitionType: "",
                        siteHost: "",
                        origin: "",
                        rawTechnicalDefinitions: "",
                        cookieCreated: cookieCreated
                    },
                    onFieldsChange: checkFormValidation,
                    onFinish: (values)=>{
                        handleCookieParserFormData(values);
                    },
                    scrollToFirstError: true,
                    ...templateFormDefaultPropsSmall,
                    children: /*#__PURE__*/ _jsx(CookieParserForm, {
                        hidden: !isParserVisible,
                        form: parserForm
                    })
                })
            })
        ]
    });
};
export { TechnicalDefinitionsTable };
